import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { semantic, space, typeRole, weight } from '@repo/design-tokens';
import { Button, Card } from '@repo/design-system';
import { CameraWallTile, FrameRegionOverlay } from '@repo/ui';
import type { CameraWallFeed, FrameRegion } from '@repo/ui';

import type { Polygon } from '../../components/PolygonPlotter2/PolygonPlotter2';
import { backdropPolygonsOnCamera, byFiledOrder, regionMarker } from './observerSessionState';
import { stageTileCaption } from './observerStageModel';
import type {
  ObserverCameraGroup,
  ObserverStateByRefid,
  ObserverTip,
} from './observerSessionState';

/**
 * An entity watched from several cameras, seen whole, on the Observers stage (`observers-page-stage`,
 * FR24). Moved here from the editor pop-up's *overview* (`multi-camera-observer-view`, epic row 132)
 * rather than rebuilt beside it, so the filed order, the unlinked card, the missing-feed and
 * undecodable-picture handling and the zoom backdrop below stay one implementation.
 *
 * One tile per camera — a camera carrying several of the entity's observers appears ONCE, with all of
 * them drawn (a 360° camera presented as quadrants is the case Rob named) — each showing only THIS
 * entity's regions on that picture, and a caption naming each one as Go Live will file it, with the
 * body part it tracks. Choosing a tile opens the drawing area on that camera.
 *
 * ⚠️ **Built ahead of its design, owed ratification.** Card 21 draws the tile; the regions over it,
 * their markers and the caption beneath are an interim composition recorded in
 * `docs/specs/epic-design-system.md` §6B. Every value is a token.
 */

/** A tile whose picture arrived but could not be decoded to draw on. */
export const REGIONS_NOT_DRAWN = 'Regions could not be drawn on this picture.';
/**
 * An observer linked to NO camera. Nothing was ever asked for, so "could not be loaded" would be a
 * false claim about the operator's data: absent is not errored (spec Failure States, row 1).
 */
export const REGION_SAVED_NO_CAMERA = 'Region saved; choose a camera to draw it on.';
/** A camera the twin's stream list does not hold — a failure, never a wait. */
export const CAMERA_NOT_IN_STREAMS = "This camera is not in the twin's stream list";
/** The stream list itself could not be read, so no camera's picture can be had. */
export const CAMERA_LIST_FAILED = "The twin's camera list could not be read";

interface Props {
  entityLabel: string;
  /**
   * The entity this overview shows, and the stage's whole session — so each tile's HOVER ZOOM can
   * draw the camera's OTHER observers too, dotted behind this entity's own regions
   * (`camera-zoom-dwell-and-observers`, Rob's ruling 2026-09-30). The tile itself still draws only
   * this entity's regions. Omitted, the zoom shows only those.
   */
  entityRefid?: string;
  polygonsByEntity?: Record<string, Polygon[]>;
  observerState?: ObserverStateByRefid;
  groups: readonly ObserverCameraGroup[];
  feedById: ReadonlyMap<number, CameraWallFeed>;
  /** The twin's stream read, which decides whether a camera missing from the wall is late or gone. */
  streamsStatus: 'loading' | 'ready' | 'failed';
  cameraName: (endpointId: number) => string;
  /** The observer's filed name, body part and number — the stage's one producer of that text. */
  tipFor: (refid: string, polygon: Polygon) => ObserverTip;
  onEditCamera: (endpointId: number | null) => void;
  /** *Remove Camera* on a tile, or *Remove* on the unlinked card (`null`) — the host confirms. */
  onRemoveCamera: (endpointId: number | null) => void;
  onRefreshFeed?: (id: CameraWallFeed['id']) => void;
  /** Rendered after the last tile: the stage's *Add Camera* tile. */
  trailing?: React.ReactNode;
  className?: string;
}

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${space[3]} ${space[4]};
`;

const Grid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  /* At least 440 px a tile, so six fit in two rows of three on a 1600 px workspace — measured on
     PUL-CON's ground floor. At 320 px four columns fit and a square 360° frame shrank to 205 px. */
  grid-template-columns: repeat(auto-fill, minmax(440px, 1fr));
  gap: ${space[4]};
`;

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  min-width: 0;
`;

/* The design system's own empty card, held at the tiles' 16:9 so the grid stays even. */
const UnlinkedCard = styled(Card)`
  aspect-ratio: 16 / 9;
  ${typeRole.caption1};
  color: ${semantic.text.secondary};
`;

const Caption = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  ${typeRole.caption1};
  color: ${semantic.text.secondary};

  .title {
    font-weight: ${weight.semibold};
    color: ${semantic.text.primary};
  }
  .note {
    color: ${semantic.text.muted};
  }
`;

const UnlinkedActions = styled.span`
  display: inline-flex;
  gap: ${space[1]};
`;

const plural = (n: number) => `${n} observer${n === 1 ? '' : 's'}`;

interface Line {
  refid: string;
  polygon: Polygon;
  tip: ObserverTip;
}

const CaptionLines: React.FC<{ lines: readonly Line[]; notes: readonly string[] }> = ({
  lines,
  notes,
}) => (
  <Caption>
    {lines.map(l => (
      <li key={l.refid}>
        <span className="title">{l.tip.title}</span> · {l.tip.detail}
      </li>
    ))}
    {notes.map(note => (
      <li key={note} className="note">
        {note}
      </li>
    ))}
  </Caption>
);

interface TileProps {
  endpointId: number;
  feed: CameraWallFeed;
  lines: readonly Line[];
  /** The picture URL that failed to decode for this camera, if any. */
  undrawnUrl: string | undefined;
  /** The camera's other observers' regions, for the zoom only — already in the `outline` style. */
  others: readonly FrameRegion[] | undefined;
  onEditCamera: (endpointId: number) => void;
  onRemoveCamera: (endpointId: number) => void;
  /** The entity's name, for the Remove action's accessible name. */
  entityLabel: string;
  onRefreshFeed?: (id: CameraWallFeed['id']) => void;
  onUndrawn: (endpointId: number, url: string) => void;
}

/**
 * One camera's tile and caption. Memoised, with stable callbacks and a memoised overlay, because the
 * camera wall patches a feed every time a frame lands and `CameraWallTile`'s comparator compares
 * `onSelect` and `overlay` by identity — built inline, every tile re-rendered on every frame.
 */
const OverviewTile = React.memo(function OverviewTile({
  endpointId,
  feed,
  lines,
  undrawnUrl,
  others,
  onEditCamera,
  onRemoveCamera,
  entityLabel,
  onRefreshFeed,
  onUndrawn,
}: TileProps) {
  const onSelect = useCallback(() => onEditCamera(endpointId), [onEditCamera, endpointId]);
  const onConfigure = useCallback(() => onRemoveCamera(endpointId), [onRemoveCamera, endpointId]);
  const action = useMemo(
    () => ({ label: 'Remove Camera', ariaLabel: `Remove ${feed.name} from ${entityLabel}` }),
    [feed.name, entityLabel],
  );
  /*
   * A failed feed draws no regions, WHATEVER picture it still holds. The wall keeps the last frame
   * of a feed whose refresh failed (`error` is a live state, drawn veiled under its pill), and
   * regions over it would present a stale picture as the camera's current view (spec Failure
   * States, row 1, Errored).
   */
  const failed = feed.state === 'not_configured' || feed.state === 'error';
  const imageUrl = failed ? undefined : feed.imageUrl;
  // Undrawn only while it is THIS picture that failed: a refresh bringing a new frame tries again.
  const undrawn = !!imageUrl && undrawnUrl === imageUrl;
  const regions = useMemo<FrameRegion[]>(
    () =>
      lines.map(l => ({
        id: l.refid,
        points: l.polygon.points,
        marker: regionMarker(l.tip),
        style: 'accent',
      })),
    [lines],
  );
  const overlay = useMemo(
    () =>
      imageUrl && !undrawn ? (
        <FrameRegionOverlay
          fit="cover"
          imageUrl={imageUrl}
          regions={regions}
          onDecodeError={() => onUndrawn(endpointId, imageUrl)}
        />
      ) : undefined,
    [imageUrl, undrawn, regions, onUndrawn, endpointId],
  );
  /*
   * The zoom: this entity's regions exactly as the tile draws them, over the camera's other
   * observers as the light dotted outline (Rob, 2026-09-30) — so enlarging the picture keeps the
   * emphasis and adds the context. Painted outline-first by the overlay, so the accent is on top.
   */
  const zoomRegions = useMemo<FrameRegion[]>(
    () => [...(others ?? []), ...regions],
    [others, regions],
  );
  /*
   * "Could not be loaded" only when there is no picture at all. A failed refresh keeps the last
   * frame on the tile (its regions are not drawn over it, above), and saying it could not be loaded
   * under a picture the operator can see was false (backlog item claimed by `observers-page`).
   */
  const noPicture = stageTileCaption(feed);
  const notes = [...(noPicture ? [noPicture] : []), ...(undrawn ? [REGIONS_NOT_DRAWN] : [])];
  return (
    <Item data-endpoint={endpointId}>
      <CameraWallTile
        feed={feed}
        onSelect={onSelect}
        selectAction
        selectLabel={`Open ${feed.name}`}
        action={action}
        onConfigure={onConfigure}
        onRefresh={onRefreshFeed}
        overlay={overlay}
        zoomRegions={zoomRegions}
      />
      <CaptionLines lines={lines} notes={notes} />
    </Item>
  );
});

const ObserverSideBySide: React.FC<Props> = ({
  entityLabel,
  entityRefid,
  polygonsByEntity,
  observerState,
  groups,
  feedById,
  streamsStatus,
  cameraName,
  tipFor,
  onEditCamera,
  onRemoveCamera,
  onRefreshFeed,
  trailing,
  className,
}) => {
  // The host's handlers, reached through refs so the tiles' callbacks are stable across renders.
  const editRef = useRef(onEditCamera);
  editRef.current = onEditCamera;
  const editCamera = useCallback((endpointId: number | null) => editRef.current(endpointId), []);
  const removeRef = useRef(onRemoveCamera);
  removeRef.current = onRemoveCamera;
  const removeCamera = useCallback(
    (endpointId: number | null) => removeRef.current(endpointId),
    [],
  );

  /*
   * Pictures that arrived but would not decode, by camera AND the URL that failed — their regions
   * are not drawn and the caption says so, until a different picture arrives. Warned once per failed
   * picture while this overview is showing.
   */
  const [undrawn, setUndrawn] = useState<ReadonlyMap<number, string>>(() => new Map());
  const markUndrawn = useCallback((endpointId: number, url: string) => {
    setUndrawn(prev => {
      if (prev.get(endpointId) === url) return prev;
      console.warn(
        `Observer side by side: the picture for feed ${endpointId} could not be decoded, so its regions are not drawn.`,
      );
      return new Map(prev).set(endpointId, url);
    });
  }, []);

  /*
   * Each camera's OTHER observers — every complete region on it that is not this entity's — for the
   * tile's zoom. The drawing area's own rule for "this camera's other observers"
   * (`backdropPolygonsOnCamera`), so the zoom and the editor's dashed backdrop agree. Recomputed on an
   * edit, never per frame; the overview is not on screen while a region is being dragged.
   */
  const othersByCamera = useMemo(() => {
    const map = new Map<number, FrameRegion[]>();
    if (!entityRefid || !polygonsByEntity || !observerState) return map;
    for (const group of groups) {
      if (group.endpointId == null) continue;
      map.set(
        group.endpointId,
        backdropPolygonsOnCamera(
          group.endpointId,
          entityRefid,
          polygonsByEntity,
          observerState,
        ).map(p => ({ id: p.id, points: p.points, style: 'outline' as const })),
      );
    }
    return map;
  }, [groups, entityRefid, polygonsByEntity, observerState]);

  /*
   * Read in FILED order: within a tile by camera number, and tiles by their lowest number, so the
   * ground floor reads .1 … .12 rather than the order the configuration happened to load in
   * (measured on PUL-CON: .12, .11, .10 first). Unnumbered observers keep their draw order after the
   * numbered ones, and the no-camera block stays last. Display only — the groups themselves, and so
   * the camera-first-drawn rule the helpers pin, are unchanged.
   */
  const shown = useMemo(() => {
    const entries = groups.map(group => {
      const lines = byFiledOrder(
        group.observers.map(o => ({
          refid: o.refid,
          polygon: o.polygon,
          tip: tipFor(o.refid, o.polygon),
        })),
      );
      return { group, lines, tip: { ordinal: lines[0]?.tip.ordinal } };
    });
    return [
      ...byFiledOrder(entries.filter(e => e.group.endpointId !== null)),
      ...entries.filter(e => e.group.endpointId === null),
    ];
  }, [groups, tipFor]);

  return (
    <Root className={className}>
      <Grid role="list" aria-label={`Cameras observing ${entityLabel}`}>
        {shown.map(({ group, lines }) => {
          if (group.endpointId === null) {
            return (
              <Item key="none" data-endpoint="none">
                <UnlinkedCard surface="on-solid" empty>
                  <span>Not linked to a camera</span>
                  <UnlinkedActions>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      aria-label={`Edit ${plural(lines.length)} not linked to a camera`}
                      onClick={() => editCamera(null)}
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      aria-label={`Remove regions not linked to a camera from ${entityLabel}`}
                      onClick={() => removeCamera(null)}
                    >
                      Remove
                    </Button>
                  </UnlinkedActions>
                </UnlinkedCard>
                <CaptionLines lines={lines} notes={[REGION_SAVED_NO_CAMERA]} />
              </Item>
            );
          }
          const endpointId = group.endpointId;
          const name = cameraName(endpointId);
          /*
           * A camera the wall does not hold is a WAIT only while the stream list is still being read.
           * Once it has answered — or failed — the camera is not coming, and a skeleton would say it
           * was (card 21: a skeleton means "no frame yet", never "no frame ever").
           */
          const held = feedById.get(endpointId);
          // The wall's own feed object, passed through untouched so the tile's memo holds.
          const feed: CameraWallFeed = held
            ? held
            : streamsStatus === 'loading'
              ? { id: endpointId, name, state: 'connecting' }
              : {
                  id: endpointId,
                  name,
                  state: 'not_configured',
                  reason: streamsStatus === 'failed' ? CAMERA_LIST_FAILED : CAMERA_NOT_IN_STREAMS,
                };
          return (
            <OverviewTile
              key={endpointId}
              endpointId={endpointId}
              feed={feed}
              lines={lines}
              undrawnUrl={undrawn.get(endpointId)}
              others={othersByCamera.get(endpointId)}
              onEditCamera={editCamera}
              onRemoveCamera={removeCamera}
              entityLabel={entityLabel}
              onRefreshFeed={onRefreshFeed}
              onUndrawn={markUndrawn}
            />
          );
        })}
        {trailing && <Item>{trailing}</Item>}
      </Grid>
    </Root>
  );
};

export default ObserverSideBySide;
